'use client';

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { resetPassword, clearAuthError } from '../../redux/actions/authActions';

const ResetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required('Le mot de passe est requis')
    .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Le mot de passe doit contenir au moins une majuscule, une minuscule, un chiffre et un caractère spécial'
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Les mots de passe doivent correspondre')
    .required('La confirmation du mot de passe est requise')
});

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error, message } = useSelector(state => state.auth);
  
  const [token, setToken] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Récupérer le token du query string
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get('token');
    
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      // Rediriger vers la page de demande de réinitialisation si pas de token
      navigate('/forgot-password');
    }
    
    // Nettoyer les erreurs précédentes
    dispatch(clearAuthError());
  }, [location, navigate, dispatch]);
  
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await dispatch(resetPassword({
        token,
        newPassword: values.password
      }));
      
      setResetSuccess(true);
    } catch (error) {
      setSubmitting(false);
    }
  };
  
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  
  if (resetSuccess) {
    return (
      <div className="reset-password-success">
        <div className="success-icon">
          <i className="material-icons">check_circle</i>
        </div>
        <h2>Mot de passe réinitialisé avec succès</h2>
        <p>Votre mot de passe a été réinitialisé. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
        <Link to="/login" className="btn btn-primary btn-block">
          Se connecter
        </Link>
      </div>
    );
  }
  
  return (
    <div className="reset-password-container">
      <h2 className="auth-title">Réinitialisation du mot de passe</h2>
      <p className="auth-subtitle">Veuillez choisir un nouveau mot de passe pour votre compte</p>
      
      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}
      
      {message && (
        <div className="alert alert-success">
          {message}
        </div>
      )}
      
      <Formik
        initialValues={{ password: '', confirmPassword: '' }}
        validationSchema={ResetPasswordSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched }) => (
          <Form className="auth-form">
            <div className="form-group">
              <label htmlFor="password">Nouveau mot de passe</label>
              <div className="password-field">
                <Field
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  id="password"
                  className={`form-control ${errors.password && touched.password ? 'is-invalid' : ''}`}
                  placeholder="Nouveau mot de passe"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={toggleShowPassword}
                >
                  <i className="material-icons">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </i>
                </button>
              </div>
              <ErrorMessage name="password" component="div" className="invalid-feedback" />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
              <div className="password-field">
                <Field
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  id="confirmPassword"
                  className={`form-control ${errors.confirmPassword && touched.confirmPassword ? 'is-invalid' : ''}`}
                  placeholder="Confirmer le mot de passe"
                />
              </div>
              <ErrorMessage name="confirmPassword" component="div" className="invalid-feedback" />
            </div>
            
            <div className="password-rules">
              <p>Le mot de passe doit contenir au moins :</p>
              <ul>
                <li>8 caractères</li>
                <li>Une lettre majuscule</li>
                <li>Une lettre minuscule</li>
                <li>Un chiffre</li>
                <li>Un caractère spécial (@$!%*?&)</li>
              </ul>
            </div>
            
            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={isSubmitting || loading}
            >
              {loading ? 'Réinitialisation en cours...' : 'Réinitialiser le mot de passe'}
            </button>
          </Form>
        )}
      </Formik>
      
      <div className="auth-links">
        <p>
          <Link to="/login" className="auth-link">
            Retour à la connexion
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;